import { Bot } from "lucide-react";
import { AbroadTag, isAbroad } from "@/components/AbroadTag";
import { timeAgo } from "@/components/inbox/inboxUtils";
import { isChatLate, waitMin, FUNNELS, funnelOf } from "@/components/chat/ChatProvider";

const chip = "shrink-0 rounded px-1.5 py-px text-[10px] font-bold";
const ORIGEM = { assinar: "Assinar", ativar: "Ativar app", cliente: "Cliente" };

const tagsOf = (s) => [
  s.img_flagged && [`chat-flagged-${s.id}`, "Imagem imprópria", "bg-red-500 text-white"],
  s.blocked && [`chat-blocked-${s.id}`, "Bloqueado", "bg-red-500/20 text-red-300"],
  s.priority && [`chat-priority-${s.id}`, "Prioridade", "bg-orange-500 text-slate-950"],
  s.name_missing ? [`chat-awaiting-${s.id}`, "aguardando dados", "bg-amber-400/15 text-amber-300"]
    : !s.client && [`chat-lead-${s.id}`, "Novo lead", "bg-amber-400/15 text-amber-300"],
  s.origem && [`chat-origem-${s.id}`, ORIGEM[s.origem], s.origem === "cliente" ? "bg-sky-400/15 text-sky-300" : "bg-lime-400/15 text-lime-300"],
  s.client && [`chat-client-tag-${s.id}`, `Cliente ${s.client.panel_user}`, "bg-emerald-500/15 text-emerald-300"],
  s.app_installed_at && [`chat-app-${s.id}`, "App", "bg-[#D4FF3A]/15 text-[#D4FF3A]"],
].filter(Boolean);

const Row = ({ s, active, onPick }) => {
  const late = s.status === "open" && isChatLate(s);
  const fn = FUNNELS[funnelOf(s)];
  const tags = tagsOf(s);
  return (
    <button data-testid={`chat-session-${s.id}`} data-late={late} onClick={() => onPick(s.id)}
      className={`w-full rounded-xl border px-3 py-2 text-left transition-colors ${active ? "border-sky-500/50 bg-sky-500/10" : late ? "border-red-500/50 bg-red-500/10" : "border-[#1F293D] bg-[#111827] hover:border-sky-500/30"}`}>
      <div className="flex items-center gap-2">
        <span data-testid={`chat-name-${s.id}`} className="min-w-0 flex-1 truncate text-sm font-semibold text-white">{s.name}</span>
        {s.mode === "ai" && <Bot className="h-3.5 w-3.5 shrink-0 text-violet-300" />}
        {s.unread > 0 && <span data-testid={`chat-unread-${s.id}`} className="rounded-full bg-emerald-500 px-1.5 text-[11px] font-bold text-slate-950">{s.unread}</span>}
        <span className="shrink-0 text-[11px] text-slate-500">{timeAgo(s.last_at)}</span>
      </div>
      <div className="mt-1 flex items-center gap-1 overflow-hidden">
        <span data-testid={`chat-funnel-${s.id}`} className={`${chip} ${fn.cls}`}>{fn.label}</span>
        {tags.slice(0, 2).map(([id, l, cls]) => <span key={id} data-testid={id} className={`${chip} min-w-0 !shrink truncate ${cls}`}>{l}</span>)}
        {tags.length > 2 && <span title={tags.slice(2).map((t) => t[1]).join(" · ")} className={`${chip} bg-slate-500/15 text-slate-400`}>+{tags.length - 2}</span>}
        {isAbroad(s.phone, s.abroad || s.client?.abroad) && <AbroadTag testid={`chat-abroad-${s.id}`} />}
      </div>
      <div className="mt-1 truncate text-xs text-slate-400">{s.last_from === "ai" ? "IA: " : s.last_from === "staff" ? "Atendimento: " : ""}{s.last_text || "Iniciou o chat"}</div>
      {s.status === "open" && (s.needs_human || s.waiting_since) && (
        <div className={`mt-0.5 text-[11px] ${late ? "font-semibold text-red-400" : "text-amber-300"}`}>
          {s.needs_human ? "Pediu atendente · " : ""}{s.waiting_since ? `aguardando há ${waitMin(s)} min` : ""}
        </div>
      )}
    </button>
  );
};

export const ChatList = ({ items, active, onPick, empty }) => (
  <div data-testid="chat-session-list" className="space-y-2">
    {items.map((s) => <Row key={s.id} s={s} active={s.id === active} onPick={onPick} />)}
    {!items.length && <div data-testid="chat-list-empty" className="rounded-xl border border-dashed border-[#1F293D] py-10 text-center text-xs text-slate-500">{empty}</div>}
  </div>
);
